// Cross-cutting per-user preference: which framework a control should be
// displayed as, wherever a control reference has no obvious framework
// context of its own (TCF ids, cross-module links, dashboards). Framework
// tab-scoped views (Controls page, FrameworkMapView, etc.) are unaffected —
// they keep showing the framework the user explicitly navigated to.
//
// This is the only React Context in the codebase — added deliberately here
// instead of prop-drilling through every intermediate component between
// App.jsx and the ~14 consumers of this preference.

import { createContext, useContext } from 'react';
import { resolveControlDisplayLabel } from '../utils/controlFrameworkPreference.js';

const ControlFrameworkPreferenceContext = createContext({
  preferredFrameworkId: null,
  enabledFrameworkIds: null,
});

export function ControlFrameworkPreferenceProvider({
  preferredFrameworkId,
  enabledFrameworkIds,
  children,
}) {
  return (
    <ControlFrameworkPreferenceContext.Provider
      value={{ preferredFrameworkId: preferredFrameworkId || null, enabledFrameworkIds }}
    >
      {children}
    </ControlFrameworkPreferenceContext.Provider>
  );
}

export function useControlFrameworkPreference() {
  return useContext(ControlFrameworkPreferenceContext);
}

/**
 * Resolves a control's display label according to the current user's
 * preferred framework. For use on context-free control references (cross-
 * module links, dashboards) — NOT on framework tab-scoped views, which
 * should keep showing the framework the user explicitly navigated to.
 */
export function useResolvedControlLabel(controlId) {
  const { preferredFrameworkId, enabledFrameworkIds } = useControlFrameworkPreference();
  return resolveControlDisplayLabel(controlId, preferredFrameworkId, enabledFrameworkIds);
}
